import React, { useCallback, useContext, useEffect, useRef } from 'react';
import {
  PaymentFrequency,
  TaskType,
  TaskDirection,
  PaymentType,
  Task,
  Contact,
  Entity,
  TaskSettlementStatus,
  InvoiceStatus,
  LineItemInput,
  OtherRecipientInput,
  PaymentTerms,
  DocumentAnalysisLineItemInput,
  DocumentType,
  DocumentAnalysis,
} from '@admiin-com/ds-graphql';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { S3Upload as S3UploadCommon } from '@admiin-com/ds-common';

import { useSelectedEntity } from '../../../hooks/useSelectedEntity/useSelectedEntity';

import { TaskCreateType } from '../../components/CreateTaskButtonGroup/CreateTaskButtonGroup';
import { useAto } from '../../../hooks/useAto/useAto';
import { useResetTaskContextFromTask } from './useResetTaskContextFromTask';
import { useDocumentAnalysisToFormValues } from './useDocumentAnalysisToFormValues';

export const BillCreationContext = React.createContext<any>(null);

interface TaskCreationProviderProps {
  contact?: Contact;
  client?: Entity;
  task?: Task | undefined | null;
  children: React.ReactNode;
  onClose: () => void;
  taskType?: TaskCreateType;
  documentAnalysis?: DocumentAnalysis | null;
}

export type settlementStatus = 'Payable' | 'Refundable';
export type Signer = {
  id?: string;
  email: string;
  name: string;
  signerType: 'CONTACT' | 'ENTITY_USER' | 'GUEST';
  entityId?: string;
  secondName?: string;
  mobile?: string;
  createContact?: boolean;
  initialPages?: boolean;
  // No Need to send backend
  color?: any;
  noLookup?: boolean;
};
//TODO: is this correct for types?
// Then in your form data type
export type BillCreateFormData = {
  id?: string;
  entityId?: string;
  from: any; //TODO: types Entity or AutocompleteResult
  to: any; //TODO: types Contact or Entity
  type: TaskType;
  paymentFrequency: PaymentFrequency;
  amount: string;
  dueAt: string;
  reference: string;
  bpayReferenceNumber?: string;
  notes: string;
  direction?: TaskDirection;
  lodgementAt: string;
  documents: (S3UploadCommon | null)[];
  annotations: any;
  gstInclusive: boolean;
  numberOfPayments: number;
  settlementStatus?: TaskSettlementStatus | null;
  paymentAt: string;
  paymentTypes?: PaymentType.PAY_NOW | PaymentType.INSTALLMENTS;
  allowExtendedTerms?: boolean;
  maximumInstallments?: number;
  firstPaymentAt: string;
  lineItems: LineItemInput[];
  invoiceStatus: InvoiceStatus;
  supportDocuments: S3UploadCommon[];
  signers: Signer[];
  sendSMS: boolean;
  sendEmail: boolean;
  otherRecipients: OtherRecipientInput[];
  sendCopyToContact: boolean;
  automaticGenerateInvoice: boolean;
  automaticInvoicePaymentAt: string;
  automaticInvoicePaymentTerms: PaymentTerms;
  quoteRequiresSignature: boolean;
  futureTaskInput?: {
    from: any;
    to: any;
    amount: number;
    dueAt: string;
    reference: string;
    issuedAt: string;
    bpayReferenceNumber: string;
    paymentFrequency: PaymentFrequency;
    lineItems: DocumentAnalysisLineItemInput[];
    numberOfPayments?: number;
    settlementStatus?: TaskSettlementStatus | null;
  };
  issuedAt?: string;
  emailMessage?: string | null;
  bindingId?: string;
  partnerId?: string;
  documentType?: DocumentType;
  sendTemplate?: boolean;
  includeLetterhead?: boolean;
  templateJson?: string;
  templateHtml?: string;
  documentAnalysisId?: string | null;
  period?: string;
  year?: string;
  tasks?: BillCreateFormData[];
};
export const BillCreateFormDataDefaultValues = {
  documents: [],
  lineItems: [],
  invoiceStatus: InvoiceStatus.INVOICE,
  signers: [],
  otherRecipients: [],
  sendCopyToContact: true,
  documentAnalysisId: null,
  sendSMS: false,
};

export function TaskCreationProvider({
  children,
  client,
  contact,
  onClose,
  task,
  taskType,
  documentAnalysis,
}: TaskCreationProviderProps) {
  const pdfSignatureRef = useRef(null);

  useAto();

  const taskProp = task;

  const { convertToFormValues } = useDocumentAnalysisToFormValues(
    documentAnalysis ?? null
  );

  // @ts-ignore
  const methods = useForm<BillCreateFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: BillCreateFormDataDefaultValues,
  });

  const setTaskData = useResetTaskContextFromTask({
    setValue: methods.setValue,
    reset: methods.reset,
  });

  useEffect(() => {
    if (taskProp) {
      setTaskData(taskProp);
    }
  }, [taskProp]);

  useEffect(() => {
    if (documentAnalysis) {
      convertToFormValues().then((formValues) => {
        console.log('formValues', formValues, documentAnalysis);
        methods.reset({ ...BillCreateFormDataDefaultValues, ...formValues });
      });
    }
  }, [documentAnalysis]);

  const { control, setValue } = methods;

  const taskDirection = useWatch({
    name: 'direction',
    control,
    defaultValue: TaskDirection.SENDING,
  });

  const setTaskDirection = useCallback(
    (direction: TaskDirection) => {
      setValue('direction', direction);
    },
    [setValue]
  );

  const resetForm = () => {
    methods.reset(BillCreateFormDataDefaultValues);
  };

  const [isTaxBinding, setIsTaxBinding] = React.useState(false);

  React.useEffect(() => {
    if (taskProp?.id) {
      if (taskProp.bindingId) {
        setIsTaxBinding(true);
      } else {
        setIsTaxBinding(false);
      }
    }
  }, [taskProp?.id]);

  return (
    <BillCreationContext.Provider
      value={{
        pdfSignatureRef,
        client,
        contact,
        task: taskProp,
        taskDirection,
        setTaskDirection,
        taskType,
        onClose,
        resetForm,
        isTaxBinding,
        setIsTaxBinding,
      }}
    >
      <FormProvider {...methods}>{children}</FormProvider>
    </BillCreationContext.Provider>
  );
}

export const useDefaultValueForFromTo = () => {
  const { client } = useContext(BillCreationContext);
  const { entity } = useSelectedEntity();

  return client ?? entity;
};
